import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppList from "@toothfairy/shared-ui/AppList";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppUtilities from "@toothfairy/shared-ui/AppUtilities";
import React from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, View } from "react-native";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import { isLargeScreen } from "../../MainViewContainer";
import { isElectronRunning } from "../../../../App";

// Unified billing history: recharges (direct credit top-ups) + invoice payments
// (subscription payments). Recharges never produce a Stripe invoice, so the old
// invoices-only view hid them. This renders the normalized /billing/history
// response — one row per real money movement — sorted newest first.
const BillingHistory = ({
  billingHistory,
  stripeClientBillingHistoryData,
  loadMoreBillingHistory,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const styles = styleGenerator(currentTheme);
  const { t } = useTranslation();

  const openUrl = (url) => {
    if (!url) return;
    if (isElectronRunning()) {
      window?.electronAPI?.openUrlOnWeb(url);
    } else {
      window.open(url, "_blank");
    }
  };

  return (
    <View style={{ flexDirection: "column" }}>
      <AppList
        style={{ paddingLeft: 0, backgroundColor: "transparent" }}
        contentContainerStyle={styles.listContentContainer}
        isSeparatorVisible={true}
        items={billingHistory || []}
        keyExtractor={"id"}
        renderItem={({ item, index }) => (
          <>
            {index === 0 && (
              <View style={[styles.listItemContainer, styles.listHeader]}>
                <AppText
                  style={{ fontWeight: "bold" }}
                  wrapperStyle={
                    !isLargeScreen()
                      ? styles.largeCellWrapperStyle
                      : styles.cellWrapperStyle
                  }
                >
                  {t("billing.type")}
                </AppText>
                <AppText
                  style={{ fontWeight: "bold" }}
                  wrapperStyle={
                    !isLargeScreen()
                      ? styles.largeCellWrapperStyle
                      : styles.cellWrapperStyle
                  }
                >
                  {t("billing.status")}
                </AppText>
                {isLargeScreen() && (
                  <AppText
                    style={{ fontWeight: "bold" }}
                    wrapperStyle={styles.cellWrapperStyle}
                  >
                    {t("billing.amount")}
                  </AppText>
                )}
                {isLargeScreen() && (
                  <AppText
                    style={{ fontWeight: "bold" }}
                    wrapperStyle={styles.cellWrapperStyle}
                  >
                    {t("billing.date")}
                  </AppText>
                )}
                <AppText
                  style={{ fontWeight: "bold" }}
                  wrapperStyle={styles.cellWrapperStyle}
                >
                  {t("billing.actions")}
                </AppText>
              </View>
            )}
            <View style={styles.listItemContainer}>
              <AppText
                wrapperStyle={
                  !isLargeScreen()
                    ? styles.largeCellWrapperStyle
                    : styles.cellWrapperStyle
                }
              >
                {item.type === "invoice"
                  ? t("billing.invoice")
                  : t("billing.recharge")}
              </AppText>
              <AppText
                style={{ textTransform: "capitalize" }}
                color={currentTheme?.theme?.primary}
                fontWeight={"bold"}
                wrapperStyle={
                  !isLargeScreen()
                    ? styles.largeCellWrapperStyle
                    : styles.cellWrapperStyle
                }
              >
                {item.status}
              </AppText>
              {isLargeScreen() && (
                <AppText
                  fontWeight={"bold"}
                  wrapperStyle={styles.cellWrapperStyle}
                  style={{ textTransform: "uppercase" }}
                >
                  {`${item.amount / 100} ${item.currency}`}
                </AppText>
              )}
              {isLargeScreen() && (
                <AppText
                  fontWeight={"bold"}
                  wrapperStyle={styles.cellWrapperStyle}
                >
                  {`${AppUtilities.dateConverter.ConvertTimeStampToDateString(
                    item.created * 1000,
                    "en",
                    t,
                    false,
                    "DD MMM YYYY",
                  )}`}
                </AppText>
              )}
              <AppIcon
                style={{
                  width: isLargeScreen() ? 120 : 60,
                  flexDirection: "row",
                }}
                icon={AppAssets.icons.link}
                size={16}
                isClickable={!!item.hosted_url}
                onPress={() => openUrl(item.hosted_url)}
              />
            </View>
          </>
        )}
        ListEmptyComponent={
          <View style={{ padding: 14 }}>
            <AppText>{t("billing.noBillingHistory")}</AppText>
          </View>
        }
      />
      {stripeClientBillingHistoryData?.has_more && (
        <ToothFairyButton
          title={t("billing.loadMore")}
          wrapperStyle={{ alignSelf: "center" }}
          onPress={loadMoreBillingHistory}
        />
      )}
    </View>
  );
};
const styleGenerator = (currentTheme) =>
  StyleSheet.create({
    cellWrapperStyle: {
      justifyContent: "center",
      width: "20%",
    },
    largeCellWrapperStyle: {
      justifyContent: "center",
      width: "40%",
    },
    listContentContainer: {
      paddingBottom: 20,
    },
    listHeader: {
      backgroundColor: currentTheme?.theme?.lightGrey,
    },
    listItemContainer: {
      flexDirection: "row",
      padding: 10,
    },
  });

export default BillingHistory;
